import { PageShell } from "@superadmin/components/PageShell";
import { TeamMembersDialog } from "@superadmin/components/TeamMembersDialog";
import { formatEpoch } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Button } from "@voicely/ui-mui/form/Button";
import { Switch } from "@voicely/ui-mui/form/Switch";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { z } from "zod";

/**
 * Which workspace's members dialog is open. In the URL because a refresh, or a
 * link pasted to a colleague, should land on the same dialog. A value naming a
 * workspace that is not in the list degrades to closed.
 */
const panelZod = z
	.string()
	.regex(/^members:[\w-]+$/)
	.nullable();

export const TeamsPage = () => {
	const [panel, setPanel] = useUrlState<string | null>("panel", panelZod, null);
	const teamsQuery = useQuery(orpc.superAdmin.listTeams.queryOptions());
	const translated = useTranslatedCallsSwitch();

	if (teamsQuery.isPending) {
		return (
			<PageShell title="Workspaces">
				<LoadingSpinner />
			</PageShell>
		);
	}

	if (teamsQuery.error) {
		return (
			<PageShell title="Workspaces">
				<Alert severity="error">{teamsQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const membersOf = panel?.startsWith("members:")
		? teamsQuery.data.find((team) => team.id === panel.slice("members:".length))
		: undefined;

	return (
		<PageShell title="Workspaces" subtitle="Active workspaces, newest first">
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>Id</TableCell>
							<TableCell>Kind</TableCell>
							<TableCell>Created</TableCell>
							<TableCell>Translated calls</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{teamsQuery.data.map((team) => (
							<TableRow key={team.id} hover>
								<TableCell>{team.name}</TableCell>
								<TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{team.id}</TableCell>
								<TableCell>
									<Chip
										label={team.personalTeamForUserId ? "personal" : "shared"}
										size="small"
										color={team.personalTeamForUserId ? "default" : "info"}
									/>
								</TableCell>
								<TableCell>{formatEpoch(team.createdAt)}</TableCell>
								<TableCell>
									<Switch
										size="small"
										checked={translated.enabled.has(team.id)}
										disabled={translated.loading}
										onChange={(_, enabled) => translated.set(team.id, enabled)}
										slotProps={{ input: { "aria-label": `Translated calls for ${team.name}` } }}
									/>
								</TableCell>
								<TableCell align="right">
									<Button size="small" onClick={() => setPanel(`members:${team.id}`)}>
										Members
									</Button>
									<Button size="small" component={Link} to={`/sessions?team=${team.id}`}>
										Sessions
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			{membersOf && (
				<TeamMembersDialog
					teamId={membersOf.id}
					teamName={membersOf.name}
					onClose={() => setPanel(null)}
				/>
			)}
		</PageShell>
	);
};

/**
 * The per-workspace switch for translated calls — a separate product, off for
 * a new workspace. Turning it off shows a "contact us" page and refuses its
 * procedures; calls already made and their history stay.
 */
function useTranslatedCallsSwitch() {
	const queryClient = useQueryClient();
	const enabledTeams = useQuery(orpc.translatedCalls.adminEnabledTeams.queryOptions());
	const mutation = useMutation(
		orpc.translatedCalls.adminSetEnabled.mutationOptions({
			onSuccess: async (_, input) => {
				toast.success(`Translated calls ${input.enabled ? "on" : "off"}.`);
				await queryClient.invalidateQueries({
					queryKey: orpc.translatedCalls.adminEnabledTeams.key(),
				});
			},
			onError: (error) => toast.error(error.message),
		}),
	);

	return {
		enabled: new Set(enabledTeams.data ?? []),
		loading: enabledTeams.isPending || mutation.isPending,
		set: (teamId: string, enabled: boolean) => mutation.mutate({ teamId, enabled }),
	};
}
